import { useTranslation } from 'react-i18next';
import { Brain } from 'lucide-react';
import type { ReactNode } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@bliks-io/ui';
import type { AiReasoningEffort } from '@bliks-io/contracts';

// Static translation keys per effort so `check:translations` can statically
// verify them without traversing dynamic `${effort}` interpolations.
const REASONING_EFFORT_I18N: Record<AiReasoningEffort, string> = {
  none: 'adminSettings.globalAi.reasoning.effort.none',
  low: 'adminSettings.globalAi.reasoning.effort.low',
  medium: 'adminSettings.globalAi.reasoning.effort.medium',
  high: 'adminSettings.globalAi.reasoning.effort.high',
  xhigh: 'adminSettings.globalAi.reasoning.effort.xhigh',
  max: 'adminSettings.globalAi.reasoning.effort.max'
};

export interface ReasoningEffortSelectProps {
  /** The stored effort; a value the enum dropped renders raw. */
  effort?: string;
  /** The effort that runs while none is stored; absent on orphan rows. */
  defaultEffort?: AiReasoningEffort;
  /** Efforts the model accepts, lowest first. */
  efforts: readonly AiReasoningEffort[];
  /** Set on a row that shows another row's effort. */
  disabled?: boolean;
  onEffortChange: (effort: AiReasoningEffort | null) => void;
}

function ReasoningEffortSelect({
  effort,
  defaultEffort,
  efforts,
  disabled,
  onEffortChange
}: ReasoningEffortSelectProps) {
  const { t } = useTranslation();
  // An effort the row's model does not accept (it was set for another model)
  // still has to render, like a stale model value does; one the enum dropped
  // renders raw.
  const staleEffort =
    effort !== undefined && !(efforts as readonly string[]).includes(effort) ? effort : undefined;
  return (
    <Select
      value={effort ?? defaultEffort}
      disabled={disabled}
      // Like the model selects: picking the level that runs anyway stores
      // nothing, so the row keeps following its model's default.
      onValueChange={(value) =>
        onEffortChange(value === defaultEffort ? null : (value as AiReasoningEffort))
      }
    >
      <SelectTrigger
        aria-label={t('adminSettings.globalAi.reasoning.label')}
        allowClear={!disabled}
        onClear={() => onEffortChange(null)}
        hasValue={effort !== undefined}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Brain className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">
            <SelectValue />
          </span>
        </div>
      </SelectTrigger>
      <SelectContent>
        {efforts.map((option) => (
          <SelectItem key={option} value={option}>
            {t(REASONING_EFFORT_I18N[option])}
          </SelectItem>
        ))}
        {staleEffort !== undefined && (
          <SelectItem value={staleEffort} disabled>
            {Object.keys(REASONING_EFFORT_I18N).includes(staleEffort)
              ? t(REASONING_EFFORT_I18N[staleEffort as AiReasoningEffort])
              : staleEffort}
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

export function ModelSelectRow<T extends string>({
  label,
  description,
  value,
  onValueChange,
  options,
  labels,
  disabledOptions,
  leadingOptions,
  hasValue,
  onClear,
  reasoning
}: {
  label: ReactNode;
  description?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: readonly T[];
  labels: Record<T, string>;
  disabledOptions?: Set<T>;
  leadingOptions?: { value: string; label: ReactNode }[];
  hasValue?: boolean;
  onClear?: () => void;
  /** The effort select right of the model select; absent on non-text rows. */
  reasoning?: ReasoningEffortSelectProps;
}) {
  // A stored value dropped from the option enum still has to render (disabled),
  // or the select goes blank and the diff baseline shifts under it.
  const staleValue =
    !(options as readonly string[]).includes(value) &&
    !leadingOptions?.some((opt) => opt.value === value)
      ? value
      : undefined;
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <span className="text-base font-medium">{label}</span>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      <div className="flex items-center gap-2">
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger
            className="w-[250px]"
            allowClear={!!onClear}
            onClear={onClear}
            hasValue={hasValue}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {leadingOptions?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
            {options.map((opt) => (
              <SelectItem key={opt} value={opt} disabled={disabledOptions?.has(opt)}>
                {labels[opt]}
              </SelectItem>
            ))}
            {staleValue !== undefined && (
              <SelectItem key={staleValue} value={staleValue} disabled>
                {staleValue}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        {/* Rows without an effort keep the column, so model selects line up. */}
        <div className="w-60">{reasoning && <ReasoningEffortSelect {...reasoning} />}</div>
      </div>
    </div>
  );
}
